import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams, useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { parseEdgeFunctionErrorAsync } from "@/lib/parseEdgeFunctionError";
import { trackEvent } from "@/hooks/useEventTracking";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Loader2,
  Send,
  ArrowLeft,
  CheckCircle,
  MapPin,
  PlusCircle,
  Edit,
  AlertTriangle,
  Heart,
  BadgeCheck,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { useReverseGeocode } from "@/hooks/useReverseGeocode";
import { useAuth } from "@/hooks/useAuth";
import { useOwnedShelters } from "@/hooks/useOwnedShelters";
import { LocationPicker } from "@/components/LocationPicker";
import { TriStateToggle } from "@/components/TriStateToggle";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { RouteFallback } from "@/components/RouteFallback";
import { useConfetti } from "@/hooks/useConfetti";
import { normalizeUpdateType, trackingTypeLabel, type UpdateType } from "@/lib/updateType";
import { getSubmitSuccessMessage, SUCCESS_TITLE } from "@/lib/successMessage";
import { PrivacyNotice } from "@/components/PrivacyNotice";

const shelterSchema = z.object({
  shelter_name: z.string().trim().min(2, "Nazwa musi mieć min. 2 znaki").max(200, "Nazwa może mieć max. 200 znaków"),
  city: z.string().trim().min(2, "Miasto musi mieć min. 2 znaki").max(100, "Miasto może mieć max. 100 znaków"),
  region: z.string().trim().max(100, "Region może mieć max. 100 znaków").optional(),
  address: z.string().trim().max(300, "Adres może mieć max. 300 znaków").optional(),
  postal_code: z
    .string()
    .trim()
    .refine((val) => {
      if (!val) return true;
      return /^\d{2}-\d{3}$/.test(val);
    }, "Kod pocztowy musi mieć format XX-XXX")
    .optional()
    .or(z.literal("")),
  lat: z
    .number()
    .min(49, "Szerokość geogr. musi być w Polsce (49-55)")
    .max(55, "Szerokość geogr. musi być w Polsce (49-55)"),
  lng: z
    .number()
    .min(14, "Długość geogr. musi być w Polsce (14-24)")
    .max(24, "Długość geogr. musi być w Polsce (14-24)"),
  phone: z.string().trim().max(50, "Telefon może mieć max. 50 znaków").optional(),
  email: z.string().trim().email("Nieprawidłowy email").max(255).optional().or(z.literal("")),
  website: z
    .string()
    .trim()
    .max(500)
    .refine((val) => {
      if (!val) return true;
      // Accept URLs starting with http://, https://, or www.
      return /^(https?:\/\/|www\.)[^\s]+\.[^\s]+$/.test(val);
    }, "Nieprawidłowy URL (użyj formatu https://... lub www...)")
    .optional()
    .or(z.literal("")),
  description: z.string().trim().max(2000, "Opis może mieć max. 2000 znaków").optional(),
  current_needs: z.string().trim().max(1000, "Potrzeby mogą mieć max. 1000 znaków").optional(),
  volunteer_info: z.string().trim().max(1000, "Info o wolontariacie może mieć max. 1000 znaków").optional(),
});

interface FormData {
  shelter_id: string | null;
  shelter_name: string;
  city: string;
  region: string;
  address: string;
  postal_code: string;
  lat: string;
  lng: string;
  phone: string;
  email: string;
  website: string;
  current_needs: string;
  urgent_needs: boolean | null;
  dog_walking_available: boolean | null;
  volunteering_available: boolean | null;
  dogs_available: boolean | null;
  cats_available: boolean | null;
  volunteer_info: string;
  description: string;
  submitter_note: string;
  is_organization_request: boolean;
}


const SubmitUpdatePage = () => {
  const [searchParams] = useSearchParams();
  const { slug: slugParam } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryShelterId = searchParams.get("shelter");
  const updateType: UpdateType = normalizeUpdateType(searchParams.get("type"));
  const prefillName = searchParams.get("name") || "";
  const prefillCity = searchParams.get("city") || "";
  const ownerModeRequested = searchParams.get("owner") === "1";
  const [resolvedShelterId, setResolvedShelterId] = useState<string | null>(queryShelterId);
  const [slugResolving, setSlugResolving] = useState<boolean>(!!slugParam && !queryShelterId);
  const [slugNotFound, setSlugNotFound] = useState(false);
  const shelterId = resolvedShelterId;
  const isNewShelter = !shelterId && !slugResolving && !slugParam;
  const isNeedsUpdate = updateType === "needs";
  const isInfoUpdate = updateType === "info";
  // Owner mode: user came from the panel as a logged-in shelter representative
  const isOwnerMode = ownerModeRequested && !!user && isNewShelter;

  // Multi-shelter wyłączony - jeśli zalogowany użytkownik ma już swoje schronisko,
  // nie pozwalamy zgłaszać kolejnego nowego obiektu w trybie owner.
  const { data: ownedShelters } = useOwnedShelters(user?.id);
  useEffect(() => {
    if (isOwnerMode && ownedShelters && ownedShelters.length > 0) {
      toast.info("Możesz zarządzać tylko jednym schroniskiem na konto");
      navigate("/panel", { replace: true });
    }
  }, [isOwnerMode, ownedShelters, navigate]);

  // Resolve slug → id (and redirect old ?shelter=UUID URLs to /zglos/:slug)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (slugParam) {
        const { data } = await supabase
          .from("shelters")
          .select("id")
          .eq("slug", slugParam)
          .maybeSingle();
        if (cancelled) return;
        if (data?.id) {
          setResolvedShelterId(data.id);
        } else {
          setSlugNotFound(true);
        }
        setSlugResolving(false);
      } else if (queryShelterId) {
        // Backward-compat: redirect ?shelter=UUID → /zglos/:slug
        const { data } = await supabase
          .from("shelters")
          .select("slug")
          .eq("id", queryShelterId)
          .maybeSingle();
        if (cancelled) return;
        if (data?.slug) {
          const qs = new URLSearchParams();
          if (updateType && updateType !== "full") qs.set("type", updateType);
          navigate(`/zglos/${data.slug}${qs.toString() ? `?${qs.toString()}` : ""}`, { replace: true });
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [slugParam, queryShelterId, updateType, navigate]);


  // Track form open — czekamy aż slug→id się rozwiąże, żeby shelter_id był poprawnie powiązany
  const [trackedOpen, setTrackedOpen] = useState(false);
  useEffect(() => {
    if (slugResolving || trackedOpen) return;
    trackEvent("Formularz otwarty", {
      type: trackingTypeLabel(updateType, isNewShelter),
      type_raw: updateType,
      shelter_id: shelterId || undefined,
      slug: slugParam || undefined,
      prefilled: !!(prefillName || prefillCity) || undefined,
    });
    setTrackedOpen(true);
  }, [slugResolving, shelterId, isNewShelter, isNeedsUpdate, isInfoUpdate, slugParam, prefillName, prefillCity, trackedOpen]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(!!shelterId || slugResolving);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [geocodedAddress, setGeocodedAddress] = useState<string | null>(null);
  const { reverseGeocode, isLoading: isReverseGeocoding } = useReverseGeocode();
  const { fireSuccessConfetti } = useConfetti();
  const [formData, setFormData] = useState<FormData>({
    shelter_id: null,
    shelter_name: prefillName,
    city: prefillCity,
    region: "",
    address: "",
    postal_code: "",
    lat: "",
    lng: "",
    phone: "",
    email: "",
    website: "",
    current_needs: "",
    urgent_needs: null,
    dog_walking_available: null,
    volunteering_available: null,
    dogs_available: null,
    cats_available: null,
    volunteer_info: "",
    description: "",
    submitter_note: "",
    is_organization_request: false,
  });

  // Stan blokady dla profili zarządzanych przez organizację (has_owner=true)
  // oraz dla właściciela próbującego edytować swoje schronisko publicznym formularzem.
  const [managedShelter, setManagedShelter] = useState<{
    name: string;
    slug: string | null;
    isOwner: boolean;
  } | null>(null);

  // Load existing shelter data if updating
  useEffect(() => {
    if (slugResolving) return;
    if (shelterId) {
      const loadShelter = async () => {
        const { data, error } = await supabase.from("shelters").select("*").eq("id", shelterId).maybeSingle();

        if (data && !error) {
          // Profil aktywnie zarządzany przez organizację - blokujemy publiczny formularz.
          // Edge function i tak rzuci 403, ale chcemy lepszy UX zanim user wypełni formularz.
          if ((data as any).has_owner === true) {
            // Sprawdź czy zalogowany user jest właścicielem - wtedy redirect do panelu.
            let isOwner = false;
            if (user) {
              const { data: ownership } = await supabase
                .from("shelter_owners")
                .select("shelter_id")
                .eq("user_id", user.id)
                .eq("shelter_id", data.id)
                .maybeSingle();
              isOwner = !!ownership;
            }
            setManagedShelter({
              name: data.name,
              slug: (data as any).slug ?? null,
              isOwner,
            });
            setIsLoading(false);
            return;
          }

          setFormData({
            shelter_id: data.id,
            shelter_name: data.name,
            city: data.city,
            region: data.region || "",
            address: data.address || "",
            postal_code: (data as any).postal_code || "",
            lat: String(data.lat),
            lng: String(data.lng),
            phone: data.phone || "",
            email: data.email || "",
            website: data.website || "",
            current_needs: data.current_needs || "",
            urgent_needs: data.urgent_needs,
            dog_walking_available: data.dog_walking_available,
            volunteering_available: (data as any).volunteering_available,
            dogs_available: data.dogs_available,
            cats_available: data.cats_available,
            volunteer_info: data.volunteer_info || "",
            description: data.description || "",
            submitter_note: "",
            is_organization_request: false,
          });
        }
        setIsLoading(false);
      };
      loadShelter();
    } else {
      setIsLoading(false);
    }
  }, [shelterId, slugResolving, user]);

  // Auto-redirect właściciela do panelu (po krótkim komunikacie).
  useEffect(() => {
    if (managedShelter?.isOwner) {
      toast.info("To Twoje schronisko - zarządzaj nim w panelu");
      const t = setTimeout(() => navigate("/panel", { replace: true }), 800);
      return () => clearTimeout(t);
    }
  }, [managedShelter?.isOwner, navigate]);

  // Owner mode: force organization request flag.
  // UWAGA: nie pre-fillujemy emaila kontaktowego adresem konta - email kontaktowy
  // schroniska to zwykle inny adres niż ten, którym właściciel się loguje.
  useEffect(() => {
    if (!isOwnerMode) return;
    setFormData((prev) => ({
      ...prev,
      is_organization_request: true,
    }));
  }, [isOwnerMode]);

  const handleChange = (field: keyof FormData, value: string | boolean | null) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Gdy użytkownik zmienia miasto, wyzeruj wcześniej dobraną ulicę i koordynaty —
      // adres ulicy z innego miasta nie ma sensu i myli geokoder.
      if (field === "city" && prev.city !== value) {
        next.address = "";
        next.lat = "";
        next.lng = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [field]: "" }));
    // Clear geocoded address if location fields change
    if (["address", "city"].includes(field)) {
      setGeocodedAddress(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const lat = parseFloat(formData.lat);
    const lng = parseFloat(formData.lng);

    // Walidacja lokalizacji dla nowego schroniska lub aktualizacji info
    if (isNewShelter || isInfoUpdate) {
      if (!formData.lat || !formData.lng || isNaN(lat) || isNaN(lng)) {
        setErrors({ location: "Wskaż lokalizację schroniska na mapie" });
        toast.error("Wskaż lokalizację na mapie");
        return;
      }

      // Sprawdź czy współrzędne są w Polsce
      if (lat < 49 || lat > 55 || lng < 14 || lng > 24) {
        setErrors({ location: "Lokalizacja musi być w Polsce" });
        toast.error("Lokalizacja musi być w Polsce");
        return;
      }
    }

    // Validate with Zod
    const dataToValidate = {
      shelter_name: formData.shelter_name,
      city: formData.city,
      region: formData.region || undefined,
      address: formData.address || undefined,
      lat: isNaN(lat) ? 0 : lat,
      lng: isNaN(lng) ? 0 : lng,
      phone: formData.phone || undefined,
      email: formData.email || undefined,
      website: formData.website || undefined,
      description: formData.description || undefined,
      current_needs: formData.current_needs || undefined,
      volunteer_info: formData.volunteer_info || undefined,
    };

    const result = shelterSchema.safeParse(dataToValidate);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      toast.error("Popraw błędy w formularzu");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error, response } = await supabase.functions.invoke("submit-shelter-update", {
        body: {
          shelter_id: formData.shelter_id,
          shelter_name: formData.shelter_name.trim(),
          city: formData.city.trim(),
          region: formData.region.trim() || null,
          address: formData.address.trim() || null,
          postal_code: formData.postal_code.trim() || null,
          lat,
          lng,
          phone: formData.phone.trim() || null,
          email: formData.email.trim() || null,
          website: formData.website.trim()
            ? formData.website.trim().toLowerCase().startsWith("www.")
              ? "https://" + formData.website.trim()
              : formData.website.trim()
            : null,
          current_needs: formData.current_needs.trim() || null,
          urgent_needs: formData.urgent_needs,
          dog_walking_available: formData.dog_walking_available,
          volunteering_available: formData.volunteering_available,
          dogs_available: formData.dogs_available,
          cats_available: formData.cats_available,
          volunteer_info: formData.volunteer_info.trim() || null,
          description: formData.description.trim() || null,
          submitter_note: formData.submitter_note.trim() || null,
          is_organization_request: formData.is_organization_request,
        },
      });

      if (error) {
        // Prefer the raw response body from invoke() when available
        if (response) {
          try {
            const text = await response.clone().text();
            const parsed = JSON.parse(text);
            if (parsed?.error) {
              toast.error(String(parsed.error));
              return;
            }
          } catch {
            // ignore
          }
        }

        toast.error(await parseEdgeFunctionErrorAsync(error));
        return;
      }
      if (data?.error) throw new Error(data.error);

      if (isNewShelter) {
        const missing: string[] = [];
        if (!formData.address.trim()) missing.push("Adres");
        if (!formData.postal_code.trim()) missing.push("Kod pocztowy");
        if (!formData.phone.trim()) missing.push("Telefon");
        if (!formData.email.trim()) missing.push("Email");
        if (!formData.website.trim()) missing.push("Strona / Facebook");
        if (!formData.current_needs.trim()) missing.push("Aktualne potrzeby");
        if (!formData.volunteer_info.trim()) missing.push("Informacje o wolontariacie");
        if (!formData.description.trim()) missing.push("Opis schroniska");
        setMissingFields(missing);
      } else {
        setMissingFields([]);
      }
      setIsSubmitted(true);

      trackEvent("Formularz wysłany", {
        type: trackingTypeLabel(updateType, isNewShelter),
        type_raw: updateType,
        shelter_id: shelterId || undefined,
        slug: slugParam || undefined,
      });

      // Fire success confetti!
      fireSuccessConfetti();

      toast.success("Zgłoszenie wysłane pomyślnie! 🎉");
    } catch (error: any) {
      toast.error(await parseEdgeFunctionErrorAsync(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <RouteFallback />;
  }

  if (slugNotFound) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Helmet>
          <title>Nie znaleziono schroniska – GdzieSchronisko</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Card className="w-full max-w-md text-center">
          <CardContent className="py-12">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h1 className="font-display font-bold text-xl mb-2">Nie znaleziono schroniska</h1>
            <p className="text-muted-foreground mb-6">
              Adres <span className="font-mono break-all">/zglos/{slugParam}</span> nie istnieje.
              Schronisko mogło zmienić nazwę albo link jest nieprawidłowy.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Link to="/">
                <Button>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Wróć do mapy
                </Button>
              </Link>
              <Link to="/zglos">
                <Button variant="outline">
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Zgłoś nowe schronisko
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (managedShelter) {
    const slugLink = managedShelter.slug ? `/schronisko/${managedShelter.slug}` : "/";
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Helmet>
          <title>Profil zarządzany przez organizację - GdzieSchronisko</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Card className="w-full max-w-md text-center">
          <CardContent className="py-12">
            <BadgeCheck className="w-16 h-16 mx-auto mb-4 text-primary" />
            <h1 className="font-display font-bold text-xl mb-2">Profil zarządzany przez organizację</h1>
            <p className="text-muted-foreground mb-6">
              <strong>{managedShelter.name}</strong> jest aktywnie zarządzane przez przedstawiciela organizacji.
              Aktualizacje danych pochodzą bezpośrednio od niej, więc publiczny formularz został wyłączony.
              Jeśli widzisz błąd lub nieaktualną informację - zgłoś to przyciskiem „Zgłoś problem" na profilu schroniska.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Link to={slugLink}>
                <Button>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Wróć na profil
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSubmitted) {
    const hasMissing = missingFields.length > 0;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg text-center">
          <CardContent className="py-10">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-primary" />
            <h2 className="font-display font-bold text-xl mb-2">{SUCCESS_TITLE}</h2>
            <p className="text-muted-foreground mb-6">
              {getSubmitSuccessMessage({ isNewShelter, updateType })}
            </p>

            {hasMissing && (
              <div className="text-left rounded-lg border border-primary/20 bg-primary/5 p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-primary shrink-0" />
                  <p className="text-sm font-semibold text-foreground">
                    Brakuje kilku informacji
                  </p>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Im więcej danych uzupełnisz, tym łatwiej innym ludziom będzie pomóc temu schronisku. Nie wypełniono:
                </p>
                <ul className="text-xs text-foreground space-y-1 mb-3 list-disc list-inside">
                  {missingFields.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setIsSubmitted(false);
                    setMissingFields([]);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Wróć do formularza i uzupełnij
                </Button>
              </div>
            )}

            <Link to="/">
              <Button variant={hasMissing ? "ghost" : "default"}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Wróć do mapy
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dynamiczne metadane OG/Twitter na podstawie schroniska (gdy znamy slug)
  const shelterName = formData.shelter_name?.trim();
  const shelterCity = formData.city?.trim();
  const metaTitle = isNewShelter
    ? "Zgłoś nowe schronisko – GdzieSchronisko"
    : shelterName
      ? `${isNeedsUpdate ? "Zaktualizuj potrzeby" : isInfoUpdate ? "Zaktualizuj dane" : "Aktualizuj"}: ${shelterName}${shelterCity ? `, ${shelterCity}` : ""} – GdzieSchronisko`
      : "Zaktualizuj schronisko – GdzieSchronisko";
  const metaDescription = isNewShelter
    ? "Dodaj nowe schronisko do mapy GdzieSchronisko i pomóż zwierzętom."
    : shelterName
      ? `Pomóż schronisku ${shelterName}${shelterCity ? ` w ${shelterCity}` : ""}. Zaktualizuj informacje o aktualnych potrzebach, wolontariacie i adopcjach.`
      : "Zaktualizuj informacje o schronisku, jego potrzebach i wolontariacie na mapie GdzieSchronisko.";
  const canonicalUrl = slugParam
    ? `https://gdzieschronisko.pl/zglos/${slugParam}${updateType && updateType !== "full" ? `?type=${updateType}` : ""}`
    : "https://gdzieschronisko.pl/zglos";

  return (
    <div className="min-h-screen bg-background py-4 sm:py-8 px-3 sm:px-4">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="noindex, nofollow" />
        {/* Open Graph (działa nawet z noindex — tylko dla podglądu w social) */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="GdzieSchronisko" />
        <meta property="og:locale" content="pl_PL" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <div className="max-w-2xl mx-auto pb-24">
        <PageBreadcrumbs 
          currentPage={isNewShelter ? "Zgłoś schronisko" : isNeedsUpdate ? "Aktualizuj potrzeby" : "Aktualizuj dane"}
        />

        <Card>
          <CardHeader className="p-2.5 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-xl">
              {isNewShelter ? (
                <>
                  <PlusCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  Zgłoś nowe schronisko
                </>
              ) : isNeedsUpdate ? (
                <>
                  <Heart className="w-4 h-4 sm:w-5 sm:h-5" />
                  Zaktualizuj potrzeby schroniska
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4 sm:w-5 sm:h-5" />
                  Zaktualizuj dane schroniska
                </>
              )}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {isNewShelter
                ? "Wypełnij formularz, aby dodać nowe schronisko do bazy."
                : isNeedsUpdate
                  ? "Zaktualizuj informacje o potrzebach, wolontariacie i dostępności zwierząt."
                  : "Zaktualizuj dane kontaktowe i adresowe schroniska."}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-2.5 sm:p-6 pt-0 sm:pt-0">
            {isOwnerMode && (
              <div className="mb-3 sm:mb-5 flex items-start gap-2 sm:gap-3 p-2.5 sm:p-4 rounded-lg border border-primary/30 bg-primary/5">
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-primary mt-0.5 shrink-0" />
                <div className="text-xs sm:text-sm">
                  <p className="font-semibold text-foreground">Zgłaszasz schronisko jako jego przedstawiciel</p>
                  <p className="text-muted-foreground mt-1">
                    Po akceptacji przez administratora automatycznie otrzymasz dostęp do zarządzania
                    profilem schroniska w panelu. Decyzję wyślemy na adres <strong>{user?.email}</strong>.
                  </p>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-6 [&_label]:text-xs sm:[&_label]:text-sm [&_input]:h-9 sm:[&_input]:h-10 [&_input]:text-base sm:[&_input]:text-sm [&_textarea]:text-base sm:[&_textarea]:text-sm">
              {/* Basic Info - show for new shelter or info update */}
              {(isNewShelter || isInfoUpdate) && (
                <div className="space-y-2.5 sm:space-y-4">
                  <h3 className="text-sm sm:text-base font-semibold">Dane podstawowe</h3>

                  <div className="grid gap-2.5 sm:gap-4 sm:grid-cols-2">
                    <div className="space-y-1 sm:space-y-2">
                      <Label htmlFor="shelter_name">Nazwa schroniska *</Label>
                      <Input
                        id="shelter_name"
                        value={formData.shelter_name}
                        onChange={(e) => handleChange("shelter_name", e.target.value)}
                        placeholder="np. Schronisko dla Zwierząt"
                        required
                      />
                      {errors.shelter_name && <p className="text-xs text-destructive">{errors.shelter_name}</p>}
                    </div>
                    <div className="space-y-1 sm:space-y-2">
                      <Label htmlFor="city">Miasto *</Label>
                      <AddressAutocomplete
                        id="city"
                        value={formData.city}
                        onChange={(value) => handleChange("city", value)}
                        onSuggestionSelect={(suggestion) => {
                          handleChange("city", suggestion.city);
                          if (suggestion.region) {
                            handleChange("region", suggestion.region);
                          }
                          if (suggestion.postalCode) {
                            handleChange("postal_code", suggestion.postalCode);
                          }
                          if (suggestion.lat && suggestion.lng) {
                            handleChange("lat", suggestion.lat.toFixed(6));
                            handleChange("lng", suggestion.lng.toFixed(6));
                          }
                        }}
                        placeholder="np. Warszawa"
                        required
                        type="city"
                      />
                      {errors.city && <p className="text-xs text-destructive">{errors.city}</p>}
                    </div>
                  </div>

                  <div className="grid gap-2.5 sm:gap-4 sm:grid-cols-2">
                    <div className="space-y-1 sm:space-y-2">
                      <Label htmlFor="region">Województwo</Label>
                      <Input
                        id="region"
                        value={formData.region}
                        onChange={(e) => handleChange("region", e.target.value)}
                        placeholder="np. mazowieckie"
                      />

                      {errors.region && <p className="text-xs text-destructive">{errors.region}</p>}
                    </div>
                    <div className="space-y-1 sm:space-y-2">
                      <Label htmlFor="address">Ulica</Label>
                      <AddressAutocomplete
                        id="address"
                        value={formData.address}
                        onChange={(value) => handleChange("address", value)}
                        onSuggestionSelect={(suggestion) => {
                          if (suggestion.address) {
                            handleChange("address", suggestion.address);
                          }
                          if (suggestion.city) {
                            handleChange("city", suggestion.city);
                          }
                          if (suggestion.region) {
                            handleChange("region", suggestion.region);
                          }
                          if (suggestion.postalCode) {
                            handleChange("postal_code", suggestion.postalCode);
                          }
                          if (suggestion.lat && suggestion.lng) {
                            handleChange("lat", suggestion.lat.toFixed(6));
                            handleChange("lng", suggestion.lng.toFixed(6));
                          }
                        }}
                        placeholder="np. Słoneczna"
                        type="address"
                        filterByCity={formData.city}
                      />

                      {errors.address && <p className="text-xs text-destructive">{errors.address}</p>}
                    </div>
                    <div className="space-y-1 sm:space-y-2">
                      <Label htmlFor="postal_code">Kod pocztowy</Label>
                      <Input
                        id="postal_code"
                        value={formData.postal_code}
                        onChange={(e) => handleChange("postal_code", e.target.value)}
                        placeholder="np. 00-001"
                        maxLength={6}
                      />
                      {errors.postal_code && <p className="text-xs text-destructive">{errors.postal_code}</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* Location - show for new shelter or info update */}
              {(isNewShelter || isInfoUpdate) && (
                <div className="space-y-2.5 sm:space-y-4">
                  <h3 className="text-sm sm:text-base font-semibold flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    Lokalizacja na mapie
                  </h3>

                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Kliknij na mapę lub przeciągnij pinezkę, aby wskazać lokalizację schroniska. Adres uzupełni się
                    automatycznie.
                  </p>

                  {errors.location && (
                    <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0" />
                      <p className="text-sm text-destructive">{errors.location}</p>
                    </div>
                  )}

                  <LocationPicker
                    lat={formData.lat ? parseFloat(formData.lat) : null}
                    lng={formData.lng ? parseFloat(formData.lng) : null}
                    onLocationSelect={(lat, lng) => {
                      setFormData((prev) => ({
                        ...prev,
                        lat: lat.toString(),
                        lng: lng.toString(),
                      }));
                      setErrors((prev) => ({ ...prev, lat: "", lng: "", location: "" }));
                    }}
                    onReverseGeocode={async (lat, lng) => {
                      const result = await reverseGeocode(lat, lng);
                      if (result) {
                        setFormData((prev) => ({
                          ...prev,
                          city: result.city || prev.city,
                          region: result.region || prev.region,
                          address: result.address || prev.address,
                          postal_code: result.postalCode || prev.postal_code,
                        }));
                        setGeocodedAddress(result.displayName);
                        toast.success("Adres uzupełniony z mapy");
                      }
                    }}
                  />
                  {isReverseGeocoding && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Pobieranie adresu z mapy...
                    </p>
                  )}
                </div>
              )}
              {/* Contact - show for new shelter or info update */}
              {(isNewShelter || isInfoUpdate) && (
                <div className="space-y-2.5 sm:space-y-4">
                  <h3 className="text-sm sm:text-base font-semibold">Dane kontaktowe</h3>

                  <div className="grid gap-2.5 sm:gap-4 sm:grid-cols-2">
                    <div className="space-y-1 sm:space-y-2">
                      <Label htmlFor="phone">Telefon</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="np. +48 123 456 789"
                      />
                      {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
                    </div>
                    <div className="space-y-1 sm:space-y-2">
                      <Label htmlFor="email">Email schroniska</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="np. kontakt@schronisko.pl"
                      />
                      {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="website">Strona internetowa</Label>
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={(e) => handleChange("website", e.target.value)}
                      placeholder="np. https://schronisko.pl"
                    />
                    {errors.website && <p className="text-xs text-destructive">{errors.website}</p>}
                  </div>
                </div>
              )}

              {/* Needs section */}
              {(isNewShelter || isNeedsUpdate) && (
                <div className="space-y-2.5 sm:space-y-4">
                  <h3 className="text-sm sm:text-base font-semibold">Potrzeby schroniska</h3>

                  <div className="border rounded-lg p-2.5 sm:p-4 space-y-2.5 sm:space-y-3">
                    <div className="space-y-1 sm:space-y-2">
                      <Label htmlFor="current_needs">Aktualne potrzeby</Label>
                      <Textarea
                        id="current_needs"
                        value={formData.current_needs}
                        onChange={(e) => handleChange("current_needs", e.target.value)}
                        placeholder="Czego aktualnie potrzebuje schronisko..."
                        rows={5}
                      />
                      {errors.current_needs && <p className="text-xs text-destructive">{errors.current_needs}</p>}
                      <p className="text-xs text-accent-foreground bg-accent dark:bg-accent/30 px-2 py-1.5 rounded">
                        💡 Konkretne informacje (marki, rozmiary) ułatwiają ludziom pomoc.
                      </p>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="space-y-0.5">
                        <Label htmlFor="urgent_needs" className="cursor-pointer">
                          Pilne potrzeby
                        </Label>
                        <p className="text-xs text-muted-foreground">Oznaczenie widoczne przez 30 dni</p>
                      </div>
                      <Switch
                        id="urgent_needs"
                        checked={formData.urgent_needs === true}
                        onCheckedChange={(checked) => handleChange("urgent_needs", checked)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Volunteering section */}
              {(isNewShelter || isNeedsUpdate) && (
                <div className="space-y-2.5 sm:space-y-4">
                  <h3 className="text-sm sm:text-base font-semibold">Wolontariat</h3>

                  <div className="border rounded-lg p-3 sm:p-4 space-y-2.5 sm:space-y-4">
                  <p className="text-xs text-muted-foreground -mt-1">Nie masz pewności? Zostaw „brak informacji"</p>

                    <div className="grid gap-3">
                      <TriStateToggle
                        id="volunteering"
                        label="Wolontariat możliwy"
                        value={formData.volunteering_available}
                        onChange={(val) => handleChange("volunteering_available", val)}
                      />
                      <TriStateToggle
                        id="dog_walking"
                        label="Spacery z psami"
                        value={formData.dog_walking_available}
                        onChange={(val) => handleChange("dog_walking_available", val)}
                      />
                    </div>

                    <div className="space-y-2 pt-2 border-t">
                      <Label htmlFor="volunteer_info">Dodatkowe informacje</Label>
                      <Textarea
                        id="volunteer_info"
                        value={formData.volunteer_info}
                        onChange={(e) => handleChange("volunteer_info", e.target.value)}
                        placeholder="Jak zostać wolontariuszem, godziny spacerów..."
                        rows={5}
                      />
                      {errors.volunteer_info && <p className="text-xs text-destructive">{errors.volunteer_info}</p>}
                      <p className="text-xs text-accent-foreground bg-accent dark:bg-accent/30 px-2 py-1.5 rounded">
                        💡 Kilka szczegółów robi różnicę - np. godziny spacerów, dni przyjęć czy sposób zgłoszenia.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Adoption section */}
              {(isNewShelter || isNeedsUpdate) && (
                <div className="space-y-2.5 sm:space-y-4">
                  <h3 className="text-sm sm:text-base font-semibold">Adopcje</h3>

                  <div className="border rounded-lg p-2.5 sm:p-4 space-y-2.5 sm:space-y-3">
                    <p className="text-xs text-muted-foreground -mt-1">Nie masz pewności? Zostaw „brak informacji"</p>

                    <div className="grid gap-3">
                      <TriStateToggle
                        id="dogs"
                        label="Psy do adopcji"
                        value={formData.dogs_available}
                        onChange={(val) => handleChange("dogs_available", val)}
                      />
                      <TriStateToggle
                        id="cats"
                        label="Koty do adopcji"
                        value={formData.cats_available}
                        onChange={(val) => handleChange("cats_available", val)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Description - show for new shelter or info update */}
              {(isNewShelter || isInfoUpdate) && (
                <div className="space-y-2.5 sm:space-y-4">
                  <h3 className="text-sm sm:text-base font-semibold">Opis</h3>

                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="description">Opis schroniska</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleChange("description", e.target.value)}
                      placeholder="Krótki opis schroniska..."
                      rows={3}
                    />
                    {errors.description && <p className="text-xs text-destructive">{errors.description}</p>}
                  </div>
                </div>
              )}

              {/* Submitter Note - always show */}
              <div className="space-y-2.5 sm:space-y-4 border-t pt-3 sm:pt-6">
                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="submitter_note">Komentarz do zgłoszenia (opcjonalnie)</Label>
                  <Textarea
                    id="submitter_note"
                    value={formData.submitter_note}
                    onChange={(e) => handleChange("submitter_note", e.target.value)}
                    placeholder="Dodatkowe informacje, źródło danych, uwagi..."
                    rows={3}
                    maxLength={500}
                  />
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span>
                    Ten komentarz jest widoczny tylko dla administratora i nie będzie wyświetlony na stronie.
                  </p>
                </div>

                {/* Organization verification request - hidden in owner mode (auto-set) */}
                {!isOwnerMode && (
                  <div
                    className={`flex items-start gap-2.5 sm:gap-3 p-2.5 sm:p-4 border rounded-lg cursor-pointer transition-colors ${
                      formData.is_organization_request ? "bg-primary/5 border-primary" : "hover:bg-muted/50"
                    }`}
                    onClick={() => handleChange("is_organization_request", !formData.is_organization_request)}
                  >
                    <div
                      className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        formData.is_organization_request
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-muted-foreground/50"
                      }`}
                    >
                      {formData.is_organization_request && <BadgeCheck className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm inline-flex items-center gap-1.5">
                        Reprezentuję tę organizację
                        <BadgeCheck className="w-4 h-4 text-primary" />
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Zaznacz jeśli jesteś pracownikiem lub wolontariuszem tego schroniska. W celu weryfikacji
                        skontaktujemy się bezpośrednio z organizacją, korzystając z oficjalnych danych kontaktowych
                        widocznych na stronie (np. adresu email), aby potwierdzić zgłoszenie.
                      </p>
                      <p className="text-xs text-accent-foreground bg-accent dark:bg-accent/30 px-2 py-1.5 rounded mt-2">
                        Po potwierdzeniu schronisko otrzyma oznaczenie „Zweryfikowane przez organizację"
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {isNewShelter ? "Zgłoś schronisko" : "Wyślij aktualizację"}
              </Button>

              <PrivacyNotice />
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SubmitUpdatePage;
